import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PrinterIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  BanknotesIcon,
  UserIcon,
  CalendarIcon,
  PhoneIcon,
  MapPinIcon,
  EnvelopeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { invoiceService } from '../../services/invoiceService'
import { paymentService } from '../../services/paymentService'
import { InvoiceModal } from '../../components/invoices/InvoiceModal'
import toast from 'react-hot-toast'

interface InvoiceItem {
  invoice_item_id: number
  description: string
  quantity: number
  unit_price: number
  line_total: number
}

interface SaleItem {
  sale_item_id: number
  quantity: number
  unit_price: number
  line_total: number
  products?: {
    product_name: string
    sku?: string
  } | null
}

interface PaymentAllocation {
  payment_allocation_id: number
  amount: number
  payments?: {
    payment_number: string
    payment_date: string
    payment_method?: string
  } | null
}

interface Invoice {
  invoice_id: number
  invoice_number: string
  invoice_date: string
  issue_date?: string
  due_date: string
  subtotal: number
  tax_amount: number
  total_amount: number
  balance_due?: number
  paid_amount?: number
  status: string
  vat_type?: string | null
  tax_percent?: number | null
  notes?: string | null
  terms_conditions?: string | null
  customers: {
    company_name: string | null
    first_name: string | null
    last_name: string | null
    email?: string | null
    phone?: string | null
    address?: string | null
  } | null
  sales?: {
    sale_id?: number
    sale_number?: string
    sale_items?: SaleItem[]
  } | null
  invoice_items?: InvoiceItem[]
  payment_allocations?: PaymentAllocation[]
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800'
    case 'sent':
      return 'bg-blue-100 text-blue-800'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    case 'overdue':
      return 'bg-red-100 text-red-800'
    case 'partially_paid':
      return 'bg-orange-100 text-orange-800'
    case 'cancelled':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getCustomerName = (invoice: Invoice) => {
  if (!invoice.customers) return 'Walk-in Customer'
  if (invoice.customers.company_name) return invoice.customers.company_name
  return `${invoice.customers.first_name || ''} ${invoice.customers.last_name || ''}`.trim()
}

const getItems = (invoice: Invoice): Array<{
  description: string
  quantity: number
  unit_price: number
  line_total: number
  sku?: string
}> => {
  if (invoice.invoice_items && invoice.invoice_items.length > 0) {
    return invoice.invoice_items.map((item) => ({
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      line_total: item.line_total,
    }))
  }
  if (invoice.sales?.sale_items && invoice.sales.sale_items.length > 0) {
    return invoice.sales.sale_items.map((item) => ({
      description: item.products?.product_name || 'Unknown Item',
      quantity: item.quantity,
      unit_price: item.unit_price,
      line_total: item.line_total,
      sku: item.products?.sku,
    }))
  }
  return []
}

interface PaymentMethod {
  payment_method_id: number
  method_name: string
  is_active: boolean
}

export const InvoiceDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoiceService.getById(id!),
    enabled: !!id,
  })

  const { data: methodsData } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => paymentService.getMethods(),
    enabled: showPaymentModal,
  })

  const paymentMethods: PaymentMethod[] = methodsData?.data?.data || []

  const recordPaymentMutation = useMutation({
    mutationFn: (data: { amount: number; payment_method_id?: number; reference_number?: string }) =>
      invoiceService.recordPayment(id!, data),
    onSuccess: () => {
      toast.success('Payment recorded successfully')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      setShowPaymentModal(false)
      setPaymentAmount('')
      setPaymentMethodId('')
      setReferenceNumber('')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to record payment')
    },
  })

  const invoice: Invoice | undefined = data?.data?.data
  const items = invoice ? getItems(invoice) : []
  const issueDate = invoice?.issue_date || invoice?.invoice_date
  const balanceDue = Number(invoice?.balance_due || 0)
  const canReceivePayment = balanceDue > 0 && invoice?.status !== 'cancelled'

  const handleRecordPayment = () => {
    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }
    if (amount > balanceDue) {
      toast.error('Payment amount cannot exceed balance due')
      return
    }
    if (!paymentMethodId) {
      toast.error('Please select a payment method')
      return
    }
    recordPaymentMutation.mutate({
      amount,
      payment_method_id: parseInt(paymentMethodId),
      reference_number: referenceNumber || undefined,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="text-center py-20">
        <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Invoice not found</h2>
        <p className="text-gray-500 mb-6">The invoice you are looking for does not exist.</p>
        <Link
          to="/invoices"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Invoices
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/invoices"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{invoice.invoice_number}</h1>
            <p className="text-sm text-gray-500">
              {invoice.sales?.sale_number && `Sale: ${invoice.sales.sale_number}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canReceivePayment && (
            <button
              onClick={() => {
                setPaymentAmount(balanceDue.toString())
                setShowPaymentModal(true)
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <BanknotesIcon className="w-4 h-4" />
              Receive Payment
            </button>
          )}
          <button
            onClick={() => setShowPreviewModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <PrinterIcon className="w-4 h-4" />
            Preview
          </button>
        </div>
      </div>

      {/* Status & Amount Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(invoice.status)}`}>
                {invoice.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-lg font-bold text-gray-900">KSh {Number(invoice.total_amount).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <BanknotesIcon className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Balance Due</p>
              <p className="text-lg font-bold text-gray-900">
                KSh {Number(invoice.balance_due || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <ClockIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Due Date</p>
              <p className="text-lg font-bold text-gray-900">
                {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Items */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Invoice Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{item.description}</p>
                        {item.sku && <p className="text-xs text-gray-500">SKU: {item.sku}</p>}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-600">{item.quantity}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-600">
                        KSh {item.unit_price.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                        KSh {item.line_total.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-gray-600">Subtotal</td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      KSh {Number(invoice.subtotal).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="px-6 py-3 text-right text-sm font-medium text-gray-600">
                      <span className="text-sm text-gray-600">
                        {invoice.vat_type === 'vat_exclusive' ? `VAT Excl. (${invoice.tax_percent ?? 0}%)` :
                         invoice.vat_type === 'vat_inclusive' ? `VAT Incl. (${invoice.tax_percent ?? 0}%)` : 'VAT'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      KSh {Number(invoice.tax_amount || 0).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="px-6 py-3 text-right text-base font-bold text-gray-900">Total</td>
                    <td className="px-6 py-3 text-right text-base font-bold text-primary-600">
                      KSh {Number(invoice.total_amount).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payments */}
          {invoice.payment_allocations && invoice.payment_allocations.length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">Payments</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoice.payment_allocations.map((allocation) => (
                      <tr key={allocation.payment_allocation_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {allocation.payments?.payment_number || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {allocation.payments?.payment_date
                            ? new Date(allocation.payments.payment_date).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                          {allocation.payments?.payment_method?.replace('_', ' ') || '-'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                          KSh {allocation.amount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Customer</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <UserIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{getCustomerName(invoice)}</p>
                </div>
              </div>
              {invoice.customers?.email && (
                <div className="flex items-start gap-3">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-sm text-gray-600">{invoice.customers.email}</p>
                </div>
              )}
              {invoice.customers?.phone && (
                <div className="flex items-start gap-3">
                  <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-sm text-gray-600">{invoice.customers.phone}</p>
                </div>
              )}
              {invoice.customers?.address && (
                <div className="flex items-start gap-3">
                  <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-sm text-gray-600">{invoice.customers.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Dates */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Dates</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Issue Date</p>
                  <p className="text-sm text-gray-900">{issueDate ? new Date(issueDate).toLocaleDateString() : '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className="text-sm text-gray-900">
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Linked Sale */}
          {invoice.sales?.sale_id && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Linked Sale</h3>
              <Link
                to={`/sales/${invoice.sales.sale_id}`}
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
              >
                <DocumentTextIcon className="w-4 h-4" />
                {invoice.sales.sale_number || `Sale #${invoice.sales.sale_id}`}
              </Link>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">Notes</h3>
              <p className="text-sm text-gray-600">{invoice.notes}</p>
            </div>
          )}

          {/* Terms & Conditions */}
          {invoice.terms_conditions && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">Terms & Conditions</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{invoice.terms_conditions}</p>
            </div>
          )}
        </div>
      </div>

      {/* Receive Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPaymentModal(false)} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Receive Payment</h3>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Balance Due
                    </label>
                    <p className="text-2xl font-bold text-gray-900">
                      KSh {balanceDue.toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={balanceDue}
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter amount..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={paymentMethodId}
                      onChange={(e) => setPaymentMethodId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select method...</option>
                      {paymentMethods.map((method) => (
                        <option key={method.payment_method_id} value={method.payment_method_id}>
                          {method.method_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g. cheque number, transaction ID..."
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleRecordPayment}
                  disabled={recordPaymentMutation.isPending}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {recordPaymentMutation.isPending ? 'Processing...' : 'Record Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <InvoiceModal isOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} invoice={invoice} />
      )}
    </div>
  )
}
